/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Andrew Merrill <andrew.merrill@deliciousai.com>
 */

import { FieldFunctionOptions, InMemoryCache } from '@apollo/client'
import { DateTimeConstants } from '@dai/common'
import { generatedIntrospectionSchema as introspectionSchema } from '@dai/graphql'
import dayjs from 'dayjs'

const readFromCacheWithExpiration = (
  data: any,
  {
    storeFieldName,
  }: FieldFunctionOptions<Record<string, any>, Record<string, any>>,
  numHours: number = DateTimeConstants.HOURS_DAY,
): any | undefined => {
  const expiresAt = localStorage.getItem(storeFieldName)
  if (expiresAt) {
    if (dayjs().diff(dayjs(expiresAt), 'hours') >= numHours) {
      localStorage.setItem(storeFieldName, dayjs().toISOString())
      return undefined
    }
  } else {
    localStorage.setItem(storeFieldName, dayjs().toISOString())
  }
  return data
}

const _read = {
  read: readFromCacheWithExpiration,
}

const cache = new InMemoryCache({
  possibleTypes: introspectionSchema.possibleTypes,
  typePolicies: {
    AiInsight: {
      keyFields: ['id', 'insightType', 'insightText'],
    },
    DashboardStat: {
      keyFields: false, // this was causing issues with the cache because the results has an id which is the tenantId...it was the same id for every query
    },
    DashboardStatWithPrevious: {
      keyFields: false, // this was causing issues with the cache because the results has an id which is the tenantId...it was the same id for every query
    },
    ProductDashboardStat: {
      keyFields: false, // id is upc, but the date filter matters too so we don't want to cache this
    },
    Query: {
      fields: {
        // TODO: this can be removed once we have other fields, just needed
        // to use the functions to get them in
        tenantDisplays: _read,
      },
    },
  },
})

export default cache
